import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Clock,
    Phone,
    FileText,
    Bot,
    MessageCircleQuestion,
    Loader2,
    RefreshCw
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/lib/queryClient";
import type { ReceptionistCall } from "@/shared/schema";

function formatDateTime(dateStr: string | Date) {
    return new Date(dateStr).toLocaleString("en-US", {
        month: "short", day: "numeric", hour: "numeric", minute: "numeric"
    });
}

const STATUS_COLORS: Record<string, string> = {
    pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    confirmed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    no_show: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
    ongoing: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
};

interface Props {
    restaurantId: string;
}

export function ReceptionistTab({ restaurantId }: Props) {
    const [selectedCall, setSelectedCall] = useState<ReceptionistCall | null>(null);

    // Fetch receptionist calls
    const { data: calls = [], isLoading: isCallsLoading } = useQuery<ReceptionistCall[]>({
        queryKey: [`/api/restaurant/${restaurantId}/receptionist-calls`],
        refetchInterval: 5000, // Refresh every 5 seconds for live status
    });

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">AI Receptionist</h2>
                    <p className="text-muted-foreground">Monitor and review AI-handled phone calls</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => queryClient.invalidateQueries({ queryKey: [`/api/restaurant/${restaurantId}/receptionist-calls`] })}
                    >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh Calls
                    </Button>
                </div>
            </div>

            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-semibold">Call Logs</h3>
                </div>

                {isCallsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : (calls || []).length === 0 ? (
                    <Card className="p-12 text-center text-muted-foreground border-dashed">
                        <Phone className="h-12 w-12 mx-auto mb-3 opacity-20" />
                        <p>No calls recorded yet</p>
                        <p className="text-xs">Once customers start calling, you'll see summaries here</p>
                    </Card>
                ) : (
                    <div className="grid gap-3">
                        {(calls || []).map((call) => (
                            <Card
                                key={call.id}
                                className="p-4 hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-primary/30"
                                onClick={() => setSelectedCall(call)}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{call.customerName || "Inbound Caller"}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_COLORS[call.status] || ""}`}>
                                                {call.status}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3.5 w-3.5" />
                                                {formatDateTime(call.createdAt)}
                                            </span>
                                            {call.customerPhone && (
                                                <span className="flex items-center gap-1">
                                                    <Phone className="h-3.5 w-3.5" />
                                                    {call.customerPhone}
                                                </span>
                                            )}
                                            {call.durationSeconds && (
                                                <span className="flex items-center gap-1">
                                                    <Clock className="h-3.5 w-3.5" />
                                                    {Math.floor(call.durationSeconds / 60)}m {call.durationSeconds % 60}s
                                                </span>
                                            )}
                                        </div>
                                        {call.summary && (
                                            <p className="text-sm line-clamp-2 mt-2 text-foreground/80 bg-muted/30 p-2 rounded">
                                                {call.summary}
                                            </p>
                                        )}
                                    </div>
                                    <Button variant="ghost" size="icon">
                                        <FileText className="h-4 w-4" />
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Call Detail Dialog */}
            <Dialog open={!!selectedCall} onOpenChange={(open: boolean) => !open && setSelectedCall(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bot className="h-5 w-5 text-primary" />
                            Call Details
                        </DialogTitle>
                        <DialogDescription>
                            Summary and transcript of the AI receptionist conversation
                        </DialogDescription>
                    </DialogHeader>

                    {selectedCall && (
                        <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
                            <div className="grid grid-cols-2 gap-4 text-sm bg-muted/50 p-4 rounded-lg">
                                <div>
                                    <p className="text-muted-foreground text-xs uppercase font-bold tracking-wider mb-1">Date & Time</p>
                                    <p>{formatDateTime(selectedCall.createdAt)}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs uppercase font-bold tracking-wider mb-1">Duration</p>
                                    <p>{selectedCall.durationSeconds ? `${Math.floor(selectedCall.durationSeconds / 60)}m ${selectedCall.durationSeconds % 60}s` : 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs uppercase font-bold tracking-wider mb-1">Customer</p>
                                    <p>{selectedCall.customerName || 'Anonymous'}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground text-xs uppercase font-bold tracking-wider mb-1">Phone</p>
                                    <p>{selectedCall.customerPhone || 'N/A'}</p>
                                </div>
                            </div>

                            {selectedCall.summary && (
                                <div className="space-y-2">
                                    <h4 className="font-bold text-sm flex items-center gap-2">
                                        <FileText className="h-4 w-4" />
                                        Summary
                                    </h4>
                                    <div className="bg-primary/5 border border-primary/10 p-3 rounded-lg text-sm leading-relaxed">
                                        {selectedCall.summary}
                                    </div>
                                </div>
                            )}

                            {selectedCall.transcript && (
                                <div className="space-y-2">
                                    <h4 className="font-bold text-sm flex items-center gap-2">
                                        <MessageCircleQuestion className="h-4 w-4" />
                                        Transcript
                                    </h4>
                                    <div className="bg-muted p-4 rounded-lg text-sm font-mono whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto border">
                                        {selectedCall.transcript}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
